import * as THREE from 'three'
import CANNON from 'cannon'

/*
 * Network — multiplayer sandbox layer for folio.
 * DISTRIBUTED AUTHORITY (last-touch ownership migration), one room.
 *
 *  - Every client runs folio's full physics locally (untouched feel).
 *  - Each client OWNS + broadcasts its own car pose.
 *  - Shared dynamic OBJECTS have a PER-OBJECT owner. Whoever's car last hits an
 *    object claims it from the relay (last-touch wins, the relay arbitrates so
 *    exactly one client owns it). The owner simulates that object DYNAMIC and
 *    streams its pose + velocity. Everyone else keeps the SAME body DYNAMIC too
 *    and softly FOLLOWS the stream (adopt the owner's velocity so the local
 *    solver carries it between packets, then lerp/slerp out the residual drift)
 *    — never KINEMATIC — so a second player's car can really bump it and trigger
 *    the next claim. Ownership releases back to the relay when the object sleeps.
 *  - Remote cars are injected as KINEMATIC bodies so they collide locally and can
 *    shove your owned objects (that bump, on the remote owner's machine, is what
 *    hands authority to them).
 *
 * Runs on its own requestAnimationFrame loop. Friendly co-op: loose, not strict.
 * (cannon 0.6.2 has no Quaternion.slerp — we slerp with THREE temps.)
 */

const PORT = 4099
const CHASSIS_OFFSET_Z = -0.28
const SEND_EVERY = 2
const INTERP_MS = 90        // render remote cars this many ms in the PAST (jitter buffer) — absorbs jitter/reorder
const EXTRAP_MS = 140       // cap on forward extrapolation when the next car snapshot is late
const EXTRAP_MAX_K = 1.0    // never project more than ~1 snapshot step ahead (caps overshoot under bursty/late packets)
const CAR_BUF_MAX = 20      // snapshots kept per remote car
const COLLIDER_MAX_STEP = 0.6 // max units a remote car's KINEMATIC collider may advance per frame (caps penetration -> no fling)
const COLLIDER_SNAP = 8     // collider target jump beyond this = teleport/respawn -> snap instead of velocity-chase
const MAX_WIRE_SPEED = 60   // clamp adopted streamed velocities to this magnitude (rejects garbage/flung values)
const WHEEL_SPIN_K = 3      // wheel roll rate from forward speed (cosmetic)
const ANT_SIDE_K = 0.6      // antenna side lean from steering
const ANT_PITCH_K = 1.2     // antenna back lean from speed

// distributed-authority tuning
const OBJ_LERP = 0.30        // non-owner drift correction toward the streamed pose (per frame)
const CLAIM_IMPACT_MIN = 0.5 // min |impact velocity| for MY car-hit to claim an object (ignores resting contact)
const CLAIM_DEBOUNCE = 18    // min update-ticks between MY claims on the SAME object (anti-thrash)
const WIRE_STALE = 90        // update-ticks of owner silence before a follower stops following + lets the body sleep
const RECONCILE_EPS2 = 0.09  // (0.3u)^2 — only correct a settled object from a reconcile heartbeat if it drifted past this
const REMOTE_SNAP_D2 = 1600  // (40u)^2 — remote car visual jump past this = teleport/respawn, snap instead of lerp
const SLEEPING = 2           // CANNON sleepState sentinel (AWAKE 0 / SLEEPY 1 / SLEEPING 2)

// folio car chassis box half-extents (from World/Physics.js car options)
const CAR_HALF = new CANNON.Vec3(2.03 * 0.5, 1.02 * 0.5, 1.16 * 0.5)

export default class Network
{
    constructor(_options)
    {
        this.physics = _options.physics
        this.scene = _options.scene
        this.resources = _options.resources
        this.objects = _options.objects
        this.car = _options.car
        this.controls = _options.controls
        this.camera = _options.camera
        this.afterReveal = false

        this.id = null
        this.color = null
        this.hostId = null
        this.remotes = new Map()   // id -> { container, body, wheels, antenna, buf:[{t,p,q,st,sp}], lastN, spin }
        this._tick = 0

        this.net = this._parseNetSim()   // optional network emulation (?lag=&jitter=&loss=)
        this.captureObjects()
        this.watchCarHits()
        this.connect()
        // When a backgrounded tab regains focus its sim may have drifted / missed
        // updates — ask the relay for a fresh world snapshot to re-sync.
        if (typeof document !== 'undefined')
        {
            document.addEventListener('visibilitychange', () =>
            {
                if (!document.hidden && this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify({ type: 'resync' }))
            })
        }
        const loop = () => { try { this.update() } catch (e) { console.warn('[mp] update', e) } this._raf = requestAnimationFrame(loop) }
        this._raf = requestAnimationFrame(loop)
    }

    // Optional network emulation (per-window, no restart): add ?lag=120&jitter=40&loss=5
    // to the URL. lag = one-way latency in ms applied to messages sent AND received
    // (round trips ~= 2*lag); jitter = +/- ms random swing; loss = % of outgoing dropped.
    // Zero overhead when none are set. Run it in ONE window to feel asymmetric lag.
    _parseNetSim()
    {
        let lag = 0, jitter = 0, loss = 0
        try
        {
            const q = new URLSearchParams(location.search)
            lag = Math.max(0, +q.get('lag') || 0)
            jitter = Math.max(0, +q.get('jitter') || 0)
            const l = +q.get('loss') || 0
            loss = l > 1 ? l / 100 : Math.max(0, l)     // accept "5" (=5%) or "0.05"
        }
        catch (_) {}
        const on = lag > 0 || jitter > 0 || loss > 0
        if (on)
        {
            console.log(`[mp] NET-SIM on — lag ${lag}ms ±${jitter}, ${(loss * 100).toFixed(0)}% loss`)
            try
            {
                const el = document.createElement('div')
                el.textContent = `net-sim ${lag}ms${jitter ? ' ±' + jitter : ''}${loss ? '  ' + (loss * 100).toFixed(0) + '% loss' : ''}`
                el.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:99999;font:11px/1.4 monospace;color:#fff;background:rgba(200,80,40,.85);padding:3px 8px;border-radius:4px;pointer-events:none'
                window.addEventListener('load', () => { try { document.body.appendChild(el) } catch (_) {} })
            }
            catch (_) {}
        }
        return { lag, jitter, loss, on }
    }

    _lat() { return Math.max(0, this.net.lag + (Math.random() * 2 - 1) * this.net.jitter) }

    // all outbound traffic routes through here — applies sim delay + drop when on
    _send(obj)
    {
        if (!this.ws || this.ws.readyState !== 1) return
        const data = JSON.stringify(obj)
        if (!this.net.on) { this.ws.send(data); return }
        if (this.net.loss > 0 && Math.random() < this.net.loss) return       // dropped outgoing packet
        window.setTimeout(() => { if (this.ws && this.ws.readyState === 1) this.ws.send(data) }, this._lat())
    }

    // inbound dispatch routes through here — applies sim delay when on
    _recv(m)
    {
        if (!this.net.on) { this.onMessage(m); return }
        window.setTimeout(() => this.onMessage(m), this._lat())
    }

    ownsObject(i) { return this.id != null && this.owners[i] === this.id }

    // Snapshot folio's dynamic object bodies in creation order -> index = stable
    // cross-client id (every client builds the same objects in the same order).
    // Every client keeps these DYNAMIC (no kinematic switch) — ownership is now
    // per-object, tracked in parallel arrays keyed by the same index.
    captureObjects()
    {
        const w = this.physics.world
        const chassis = this.physics.car && this.physics.car.chassis && this.physics.car.chassis.body
        this.objectBodies = w.bodies.filter((b) => b.type === CANNON.Body.DYNAMIC && b.mass > 0 && b !== chassis)
        const n = this.objectBodies.length
        this.owners = new Array(n).fill(0)        // ownerId per object (0 = relay default / no owner)
        this.ownerSeq = new Array(n).fill(0)      // monotonic per-object authority sequence
        this.wireTarget = new Array(n).fill(null) // latest streamed {p,q} for objects I don't own
        this.wireVel = new Array(n).fill(null)    // latest streamed {lin,ang}
        this.wireStamp = new Array(n).fill(-1e9)  // tick each wireTarget was last refreshed (staleness guard)
        this._lastClaim = new Array(n).fill(-1e9) // tick of my last claim per object (debounce)
        console.log('[mp] captured', n, 'dynamic objects (distributed authority)')
    }

    // Listen on every shared object body: when MY car chassis hits it hard
    // enough, claim authority (the relay arbitrates so only one client wins).
    // Listening on the OBJECT bodies (not the chassis) keeps this robust across
    // car recreate() — object bodies are stable, the chassis is looked up fresh.
    watchCarHits()
    {
        this.objectBodies.forEach((b, i) =>
        {
            b.addEventListener('collide', (ev) =>
            {
                const chassis = this.physics.car && this.physics.car.chassis && this.physics.car.chassis.body
                if (!chassis || ev.body !== chassis) return        // only MY car triggers a claim
                if (this.ownsObject(i)) return
                if (this._tick - this._lastClaim[i] < CLAIM_DEBOUNCE) return
                const impact = ev.contact ? Math.abs(ev.contact.getImpactVelocityAlongNormal()) : 0
                if (impact < CLAIM_IMPACT_MIN) return               // ignore gentle/resting contact
                this.claim(i)
            })
        })
    }

    claim(i)
    {
        if (!this.ws || this.ws.readyState !== 1 || this.ownsObject(i)) return
        this._lastClaim[i] = this._tick
        // Do NOT optimistically take ownership — wait for the relay's grant so
        // two simultaneous bumpers can't both believe they won. The body is
        // dynamic anyway, so it keeps moving believably during the ~1 RTT wait.
        this._send({ type: 'claim', obj: i })
    }

    // Unique spawn per player so two cars never drop onto the same spot (overlap =
    // collider stack = fly-out). Player 1 keeps folio's ORIGIN spawn so the START
    // button stays exactly where it is; later players spiral outward (golden angle +
    // growing radius => no two ids coincide). Only X/Y move (ground plane, Z-up);
    // the follow-camera is SNAPPED to the spot so the fading START label at the origin
    // doesn't drift across the view — the start button itself is never touched.
    spawnLocalCar()
    {
        const car = this.physics.car && this.physics.car.chassis && this.physics.car.chassis.body
        if (!car || !this.id) return
        const n = this.id - 1
        const rad = n === 0 ? 0 : 4.5 + (n - 1) * 0.7   // P1 at origin; P2+ spiral out
        const ang = n * 2.399963                         // golden angle (rad)
        const x = Math.cos(ang) * rad, y = Math.sin(ang) * rad
        car.position.x = x; car.position.y = y           // keep folio's Z (drop height); wake is left to folio's reveal
        car.velocity.set(0, 0, 0); car.angularVelocity.set(0, 0, 0)
        if (this.camera && this.camera.targetEased)      // snap follow-cam -> no label drift
        {
            this.camera.targetEased.x = x; this.camera.targetEased.y = y
            if (this.camera.target) { this.camera.target.x = x; this.camera.target.y = y }
        }
        console.log(`[mp] spawn P${this.id} at ${x.toFixed(1)}, ${y.toFixed(1)}`)
    }

    // folio's reveal.go just reset the car to origin (+600ms after START) — claim our
    // unique spawn now so it isn't wiped. Also covers a welcome that arrives later.
    onReveal()
    {
        this.afterReveal = true
        this.spawnLocalCar()
    }

    connect()
    {
        const proto = location.protocol === 'https:' ? 'wss' : 'ws'
        // PROD build (container/server.mjs): relay shares the page's origin, so a
        // single reverse proxy (Caddy) handles static + WebSocket on one port/TLS.
        // DEV (vite): the page is on another port, so dial the standalone relay :PORT.
        const url = (import.meta.env && import.meta.env.PROD)
            ? `${proto}://${location.host}`
            : `${proto}://${location.hostname}:${PORT}`
        try { this.ws = new WebSocket(url) }
        catch (_) { return }
        this.ws.addEventListener('message', (e) => { let m; try { m = JSON.parse(e.data) } catch (_) { return } this._recv(m) })
        this.ws.addEventListener('close', () =>
        {
            for (const id of [...this.remotes.keys()]) this.removeRemote(id)
            window.setTimeout(() => this.connect(), 1000)
        })
    }

    onMessage(m)
    {
        if (m.type === 'welcome')
        {
            this.id = m.id; this.color = m.color; this.hostId = m.host
            if (this.afterReveal) this.spawnLocalCar()                        // only if folio's reveal already ran; else reveal.go triggers it
            if (Array.isArray(m.owners)) for (const o of m.owners) this.applyOwner(o[0], o[1], o[2])
            if (Array.isArray(m.objStates)) this.applyObjectStates(m.objStates)   // catch up to the current world
            console.log('[mp] you are P' + m.id, m.color, '— synced ' + (m.objStates ? m.objStates.length : 0) + ' moved objects')
        }
        else if (m.type === 'sync')   // resync reply (e.g. after refocus)
        {
            if (Array.isArray(m.owners)) for (const o of m.owners) this.applyOwner(o[0], o[1], o[2])
            if (Array.isArray(m.objStates)) this.applyObjectStates(m.objStates)
        }
        else if (m.type === 'host') { this.hostId = m.host }
        else if (m.type === 'owner') { this.applyOwner(m.obj, m.id, m.seq) }
        else if (m.type === 'car') { this.recvCar(m) }
        else if (m.type === 'objects') { this.applyObjects(m.items) }
        else if (m.type === 'reconcile') { this.applyReconcile(m.states) }
        else if (m.type === 'leave') this.removeRemote(m.id)
    }

    // Authoritative ownership grant from the relay. Monotonic seq guards against
    // stale/reordered grants. Becoming the owner adopts the last streamed velocity
    // (no momentum pop). Losing ownership / release (id 0) commits the last pose
    // and stops following so the body can settle + sleep.
    applyOwner(i, id, seq)
    {
        if (i == null || i < 0 || i >= this.owners.length) return
        if (seq < this.ownerSeq[i]) return
        this.ownerSeq[i] = seq
        const was = this.owners[i]
        this.owners[i] = id
        const b = this.objectBodies[i]
        if (!b) return

        if (id === this.id && was !== this.id)
        {
            // I am now the resolver — keep the object's momentum from the wire.
            b.wakeUp()
            const wv = this.wireVel[i]
            if (wv) { _clampVel(wv.lin, MAX_WIRE_SPEED); b.velocity.set(wv.lin[0], wv.lin[1], wv.lin[2]); b.angularVelocity.set(wv.ang[0], wv.ang[1], wv.ang[2]) }
            this.wireTarget[i] = null   // stop following — I drive it now
        }
        else if (id === 0)
        {
            // released / orphaned -> commit the last streamed pose, let it sleep
            const t = this.wireTarget[i]
            if (t)
            {
                b.position.set(t.p[0], t.p[1], t.p[2])
                b.quaternion.set(t.q[0], t.q[1], t.q[2], t.q[3])
                b.velocity.set(0, 0, 0); b.angularVelocity.set(0, 0, 0)
            }
            this.wireTarget[i] = null; this.wireVel[i] = null
        }
    }

    // buffer a remote car snapshot for jitter-tolerant interpolation. Drops
    // out-of-order packets (seq guard) and resets the buffer on a teleport.
    recvCar(m)
    {
        const r = this.ensureRemote(m.id, m.color)
        const n = +m.n || 0
        if (n && n <= r.lastN) return                          // stale / reordered — drop
        r.lastN = n
        const last = r.buf.length ? r.buf[r.buf.length - 1] : null
        if (last)
        {
            const dx = m.p[0] - last.p[0], dy = m.p[1] - last.p[1], dz = m.p[2] - last.p[2]
            if (dx * dx + dy * dy + dz * dz > REMOTE_SNAP_D2) r.buf.length = 0   // teleport/respawn — snap
        }
        r.buf.push({ t: nowMs(), p: m.p, q: m.q, st: +m.st || 0, sp: +m.sp || 0 })
        if (r.buf.length > CAR_BUF_MAX) r.buf.shift()
    }

    // Store streamed object poses from their owners. The actual follow (velocity
    // adoption + lerp) happens in update() so it's frame-smoothed.
    applyObjects(items)
    {
        for (const it of items)
        {
            const i = it[0]
            if (i == null || i < 0 || i >= this.objectBodies.length) continue
            if (this.ownsObject(i)) continue                 // ignore the wire for objects I own
            this.wireTarget[i] = { p: [it[1], it[2], it[3]], q: [it[4], it[5], it[6], it[7]] }
            this.wireVel[i] = { lin: [it[8] || 0, it[9] || 0, it[10] || 0], ang: [it[11] || 0, it[12] || 0, it[13] || 0] }
            this.wireStamp[i] = this._tick
        }
    }

    // Late-join / reconnect / resync catch-up: snap moved objects to the world's
    // current state from the relay's cache, so a new or returning client matches
    // everyone else. Objects still owned by someone are set to follow the live
    // stream; unowned (settled) ones just rest in their moved spot.
    applyObjectStates(states)
    {
        for (const s of states)
        {
            const i = s[0]
            if (i == null || i < 0 || i >= this.objectBodies.length || this.ownsObject(i)) continue
            const b = this.objectBodies[i]
            b.position.set(s[1], s[2], s[3])
            b.quaternion.set(s[4], s[5], s[6], s[7])
            if (this.owners[i])   // still owned -> may be live; follow the live stream
            {
                b.velocity.set(s[8] || 0, s[9] || 0, s[10] || 0)
                b.angularVelocity.set(s[11] || 0, s[12] || 0, s[13] || 0)
                b.wakeUp()
                this.wireTarget[i] = { p: [s[1], s[2], s[3]], q: [s[4], s[5], s[6], s[7]] }
                this.wireVel[i] = { lin: [s[8] || 0, s[9] || 0, s[10] || 0], ang: [s[11] || 0, s[12] || 0, s[13] || 0] }
                this.wireStamp[i] = this._tick
            }
            else                  // unowned -> resting in its moved spot, let it settle
            {
                b.velocity.set(0, 0, 0); b.angularVelocity.set(0, 0, 0)
            }
        }
    }

    // Continuous in-session reconcile (relay heartbeat): gently correct SETTLED
    // objects that have drifted out of sync. Skips objects I own and any that are
    // currently owned/live (the owner's stream is the truth), and skips objects
    // already in place — so synced objects keep sleeping undisturbed.
    applyReconcile(states)
    {
        if (!Array.isArray(states)) return
        for (const s of states)
        {
            const i = s[0]
            if (i == null || i < 0 || i >= this.objectBodies.length) continue
            if (this.ownsObject(i) || this.owners[i]) continue        // mine, or live-owned -> leave it
            const b = this.objectBodies[i]
            const dx = s[1] - b.position.x, dy = s[2] - b.position.y, dz = s[3] - b.position.z
            if (dx * dx + dy * dy + dz * dz < RECONCILE_EPS2) continue // already in sync — don't wake it
            this.wireTarget[i] = { p: [s[1], s[2], s[3]], q: [s[4], s[5], s[6], s[7]] }
            this.wireVel[i] = { lin: [0, 0, 0], ang: [0, 0, 0] }
            this.wireStamp[i] = this._tick
        }
    }

    buildCarVisual(color)
    {
        try
        {
            const chassisObj = this.car && this.car.chassis && this.car.chassis.object
            if (chassisObj)
            {
                const group = new THREE.Object3D()

                // body — clone WITHOUT touching materials, so it keeps folio's
                // matcap shading (looks 1:1 with the local car). Includes antenna.
                const body = chassisObj.clone(true)
                body.position.set(0, 0, 0)
                body.quaternion.set(0, 0, 0, 1)
                body.traverse((n) => { if (n.isMesh) n.frustumCulled = false })
                group.add(body)

                // antenna lives inside the body clone — grab it by matching index,
                // remember its rest orientation so we can sway it
                let antenna = null
                const antLocal = this.car.antena && this.car.antena.object
                if (antLocal)
                {
                    const i = chassisObj.children.indexOf(antLocal)
                    if (i >= 0 && body.children[i]) antenna = { obj: body.children[i], base: body.children[i].quaternion.clone() }
                }

                // wheels — real offset relative to the chassis (rigid car); keep
                // each one's base orientation so we can add spin/steer on top
                const wheels = []
                const items = this.car.wheels && this.car.wheels.items
                if (items && items.length)
                {
                    const qInv = chassisObj.quaternion.clone().invert()
                    items.forEach((wObj, idx) =>
                    {
                        const w = wObj.clone(true)
                        w.position.copy(wObj.position).sub(chassisObj.position).applyQuaternion(qInv)
                        w.quaternion.copy(qInv).multiply(wObj.quaternion)
                        w.traverse((n) => { if (n.isMesh) n.frustumCulled = false })
                        group.add(w)
                        wheels.push({ obj: w, base: w.quaternion.clone(), front: idx < 2 }) // items = [FL,FR,BL,BR]
                    })
                }

                this.scene.add(group)
                console.log('[mp] remote car built (matcap, wheels, antenna)')
                return { group, wheels, antenna }
            }
        }
        catch (e) { console.warn('[mp] build remote car failed, using box', e) }

        // fallback: clearly-visible box
        const group = new THREE.Object3D()
        const box = new THREE.Mesh(new THREE.BoxGeometry(3, 5, 2.5), new THREE.MeshBasicMaterial({ color: new THREE.Color(color || '#ff00ff') }))
        box.position.z = 1.25; box.frustumCulled = false; group.add(box); this.scene.add(group)
        return { group, wheels: [], antenna: null }
    }

    ensureRemote(id, color)
    {
        let r = this.remotes.get(id)
        if (!r)
        {
            // kinematic physics body so the remote car collides with objects locally
            const body = new CANNON.Body({ mass: 0 })
            body.type = CANNON.Body.KINEMATIC
            body.addShape(new CANNON.Box(CAR_HALF))
            this.physics.world.addBody(body)
            const v = this.buildCarVisual(color)
            r = this.remotes.set(id, {
                container: v.group, wheels: v.wheels, antenna: v.antenna, body,
                buf: [], lastN: 0, spin: 0,
            }).get(id)
        }
        return r
    }

    removeRemote(id)
    {
        const r = this.remotes.get(id)
        if (!r) return
        this.scene.remove(r.container)
        try { this.physics.world.removeBody(r.body) } catch (_) {}
        this.remotes.delete(id)
    }

    update()
    {
        this._tick++

        const pc = this.physics && this.physics.car
        const car = pc && pc.chassis && pc.chassis.body
        const open = this.ws && this.ws.readyState === 1
        const sending = open && (this._tick % SEND_EVERY === 0)

        // 1) broadcast OUR car pose (folio's local physics = the truth for our car)
        if (sending && car)
        {
            const p = car.position, q = car.quaternion
            this._send({ type: 'car', n: (this._carN = (this._carN | 0) + 1), p: [r2(p.x), r2(p.y), r2(p.z)], q: [r3(q.x), r3(q.y), r3(q.z), r3(q.w)], st: r3(pc.steering || 0), sp: r2(pc.forwardSpeed || 0) })
        }

        // 2) stream the objects I OWN; release any that have fallen asleep
        if (open && this.objectBodies && this.id != null)
        {
            const items = []
            for (let i = 0; i < this.objectBodies.length; i++)
            {
                if (this.owners[i] !== this.id) continue
                const b = this.objectBodies[i]
                if (b.sleepState === SLEEPING)
                {
                    this.owners[i] = 0                              // give it back to the relay
                    const rp = b.position, rq = b.quaternion        // ship the resting pose so the relay caches it
                    this._send({ type: 'release', obj: i, p: [r2(rp.x), r2(rp.y), r2(rp.z)], q: [r3(rq.x), r3(rq.y), r3(rq.z), r3(rq.w)] })
                    continue
                }
                if (sending)
                {
                    const p = b.position, q = b.quaternion, v = b.velocity, av = b.angularVelocity
                    items.push([i, r2(p.x), r2(p.y), r2(p.z), r3(q.x), r3(q.y), r3(q.z), r3(q.w), r2(v.x), r2(v.y), r2(v.z), r2(av.x), r2(av.y), r2(av.z)])
                }
            }
            if (sending && items.length) this._send({ type: 'objects', items })
        }

        // 3) follow objects I do NOT own: keep them DYNAMIC, adopt the owner's
        //    velocity (the local solver carries them between packets) and lerp/slerp
        //    out the residual drift toward the streamed pose. Never kinematic — so
        //    a local car bump produces a real contact that triggers the next claim.
        if (this.objectBodies)
        {
            for (let i = 0; i < this.objectBodies.length; i++)
            {
                const t = this.wireTarget[i]
                if (!t || this.ownsObject(i)) continue
                const b = this.objectBodies[i]
                if (this._tick - this.wireStamp[i] > WIRE_STALE)
                {
                    // owner went silent (left / backgrounded) without a clean release —
                    // stop following so the body can settle + sleep instead of drifting forever
                    this.wireTarget[i] = null; this.wireVel[i] = null
                    b.velocity.set(0, 0, 0); b.angularVelocity.set(0, 0, 0)
                    continue
                }
                b.wakeUp()
                const v = this.wireVel[i]
                if (v) { _clampVel(v.lin, MAX_WIRE_SPEED); b.velocity.set(v.lin[0], v.lin[1], v.lin[2]); b.angularVelocity.set(v.ang[0], v.ang[1], v.ang[2]) }
                b.position.x += (t.p[0] - b.position.x) * OBJ_LERP
                b.position.y += (t.p[1] - b.position.y) * OBJ_LERP
                b.position.z += (t.p[2] - b.position.z) * OBJ_LERP
                _tq.set(b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w)
                _oq.set(t.q[0], t.q[1], t.q[2], t.q[3])
                _tq.slerp(_oq, OBJ_LERP)
                b.quaternion.set(_tq.x, _tq.y, _tq.z, _tq.w)
            }
        }

        // 4) render remote cars from a small jitter buffer: interpolate the pose at
        //    (now - INTERP_MS) between the two real snapshots that bracket it, so
        //    bursty / reordered arrival under lag stays smooth. The kinematic collider
        //    follows the SAME interpolated pose (what you see = what collides).
        const _now = nowMs()
        const renderT = _now - INTERP_MS
        const frameDt = Math.min(1 / 30, Math.max(1 / 120, (_now - (this._lastUpdMs || _now)) / 1000))
        this._lastUpdMs = _now
        for (const r of this.remotes.values())
        {
            const buf = r.buf
            if (!buf.length) continue

            let px, py, pz, st, sp
            if (buf.length === 1 || renderT <= buf[0].t)
            {
                const s = buf[0]
                px = s.p[0]; py = s.p[1]; pz = s.p[2]; st = s.st; sp = s.sp
                _qa.set(s.q[0], s.q[1], s.q[2], s.q[3])
            }
            else if (renderT >= buf[buf.length - 1].t)
            {
                // ran past the newest snapshot — extrapolate from the last two, capped
                const a = buf[buf.length - 2], b = buf[buf.length - 1]
                // floor the denominator (bursty/reordered pairs collapse b.t-a.t and blow k up)
                // and cap k so we never project more than ~1 step ahead — no overshoot fling
                const k = Math.min(Math.min(renderT - b.t, EXTRAP_MS) / Math.max(20, b.t - a.t), EXTRAP_MAX_K)
                px = b.p[0] + (b.p[0] - a.p[0]) * k
                py = b.p[1] + (b.p[1] - a.p[1]) * k
                pz = b.p[2] + (b.p[2] - a.p[2]) * k
                st = b.st; sp = b.sp
                _qa.set(b.q[0], b.q[1], b.q[2], b.q[3])
            }
            else
            {
                let i = buf.length - 2
                while (i > 0 && buf[i].t > renderT) i--
                const a = buf[i], b = buf[i + 1]
                const f = (renderT - a.t) / Math.max(1, b.t - a.t)
                px = a.p[0] + (b.p[0] - a.p[0]) * f
                py = a.p[1] + (b.p[1] - a.p[1]) * f
                pz = a.p[2] + (b.p[2] - a.p[2]) * f
                st = a.st + (b.st - a.st) * f
                sp = a.sp + (b.sp - a.sp) * f
                _qa.set(a.q[0], a.q[1], a.q[2], a.q[3])
                _qb.set(b.q[0], b.q[1], b.q[2], b.q[3])
                _qa.slerp(_qb, f)
            }

            // VISUAL: exact interpolated pose (smooth).
            r.container.position.set(px, py, pz + CHASSIS_OFFSET_Z)
            r.container.quaternion.copy(_qa)

            // COLLIDER: drive by VELOCITY (capped), letting cannon integrate it, instead
            // of a zero-velocity position teleport. A teleport that overlaps the local car
            // depenetrates explosively at the default 1e7 contact stiffness (the fly-out);
            // a capped finite velocity keeps penetration shallow so it reads as a firm bump.
            const cb = r.body.position
            const sx = px - cb.x, sy = py - cb.y, sz = pz - cb.z
            const sd = Math.sqrt(sx * sx + sy * sy + sz * sz)
            if (!r._seeded || sd > COLLIDER_SNAP)
            {
                cb.set(px, py, pz); r.body.velocity.set(0, 0, 0); r._seeded = true   // first frame / teleport — snap
            }
            else
            {
                const vmul = (sd > COLLIDER_MAX_STEP ? COLLIDER_MAX_STEP / sd : 1) / frameDt
                r.body.velocity.set(sx * vmul, sy * vmul, sz * vmul)
            }
            r.body.angularVelocity.set(0, 0, 0)
            r.body.quaternion.set(_qa.x, _qa.y, _qa.z, _qa.w)

            // wheels: spin all about the axle (body Y), steer the front pair about up (body Z)
            r.spin += (sp || 0) * WHEEL_SPIN_K
            if (r.wheels)
            {
                _spinQ.setFromAxisAngle(_AXLE, r.spin)
                for (const wh of r.wheels)
                {
                    if (wh.front)
                    {
                        _steerQ.setFromAxisAngle(_UP, -(st || 0))
                        wh.obj.quaternion.copy(_steerQ).multiply(_spinQ).multiply(wh.base)
                    }
                    else wh.obj.quaternion.copy(_spinQ).multiply(wh.base)
                }
            }

            // antenna: gentle sway — lean back with speed, sideways with steering
            if (r.antenna)
            {
                _antEuler.set(-(st || 0) * ANT_SIDE_K, -(sp || 0) * ANT_PITCH_K, 0)
                _antQ.setFromEuler(_antEuler)
                r.antenna.obj.quaternion.copy(_antQ).multiply(r.antenna.base)
            }
        }

        // diagnostic: remotes + how many objects I currently own
        if (this._tick % 120 === 0 && (this.remotes.size || this.id != null))
        {
            let mine = 0; if (this.owners) for (const o of this.owners) if (o === this.id) mine++
            console.log(`[mp] P${this.id} remotes=${this.remotes.size} ownObjects=${mine}`)
        }
    }
}

const _spinQ = new THREE.Quaternion()
const _steerQ = new THREE.Quaternion()
const _antQ = new THREE.Quaternion()
const _antEuler = new THREE.Euler()
const _AXLE = new THREE.Vector3(0, 1, 0)
const _UP = new THREE.Vector3(0, 0, 1)
const _tq = new THREE.Quaternion()   // object-follower slerp (cannon 0.6.2 lacks Quaternion.slerp)
const _oq = new THREE.Quaternion()
const _qa = new THREE.Quaternion()   // remote-car interpolation slerp
const _qb = new THREE.Quaternion()
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())
function _clampVel(v, max) { const m2 = v[0] * v[0] + v[1] * v[1] + v[2] * v[2]; if (m2 > max * max) { const s = max / Math.sqrt(m2); v[0] *= s; v[1] *= s; v[2] *= s } return v }
function r2(n) { return Math.round(n * 100) / 100 }
function r3(n) { return Math.round(n * 1000) / 1000 }
